import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const STATUS_LABELS = {
  active:  { label: 'Actif',    color: 'var(--success)',      bg: 'var(--success-light)' },
  pending: { label: 'En attente', color: 'var(--warning)',    bg: 'var(--warning-light)' },
  expired: { label: 'Expiré',   color: 'var(--text-muted)',   bg: 'var(--bg)' },
};

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const METHODS = {
  orange_money: '🟠 Orange Money',
  mtn_money:    '🟡 MTN Money',
  wave:         '🔵 Wave',
  moov_money:   '🟢 Moov Money',
};

export default function Subscriptions() {
  const [data, setData] = useState({ subscriptions: [], total: 0, stats: null });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: 25 });
    if (status) params.set('status', status);
    api.get(`/admin/subscriptions?${params}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const { subscriptions, stats } = data;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title">Abonnements</h2>
          <p className="page-subtitle">Flux d'abonnements des prestataires</p>
        </div>
      </div>

      {/* KPI */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total',   value: stats.total,   icon: '📋', color: 'var(--info)' },
            { label: 'Actifs',  value: stats.active,  icon: '✅', color: 'var(--success)' },
            { label: 'En attente', value: stats.pending, icon: '⏳', color: 'var(--warning)' },
            { label: 'Revenus', value: `${fmt(stats.revenue)} FCFA`, icon: '💰', color: 'var(--primary)' },
          ].map(k => (
            <div key={k.label} className="card animate-fade" style={{ padding: '20px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', fontWeight: 600 }}>{k.label}</span>
                <span style={{ fontSize: 20 }}>{k.icon}</span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div className="card animate-fade" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)', marginRight: 4 }}>Filtrer :</span>
          {[
            { val: '',        label: 'Tous' },
            { val: 'active',  label: '✅ Actifs' },
            { val: 'pending', label: '⏳ En attente' },
            { val: 'expired', label: '⬛ Expirés' },
          ].map(f => (
            <button
              key={f.val}
              onClick={() => setStatus(f.val)}
              style={{
                padding: '6px 16px', borderRadius: 'var(--radius-full)',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: status === f.val ? '2px solid var(--primary)' : '1.5px solid var(--border)',
                background: status === f.val ? 'var(--primary-light)' : 'var(--surface)',
                color: status === f.val ? 'var(--primary)' : 'var(--text-secondary)',
              }}
            >{f.label}</button>
          ))}
          <button onClick={load} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }} title="Rafraîchir">🔄</button>
        </div>
      </div>

      {/* Tableau */}
      <div className="card animate-fade">
        <div className="card-header">
          <span className="card-title">Historique ({data.total})</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />
              Chargement...
            </div>
          ) : subscriptions.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
              <div style={{ fontWeight: 600 }}>Aucun abonnement trouvé</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Prestataire', 'Méthode', 'Montant', 'Début', 'Fin', 'Durée', 'Référence', 'Statut'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(sub => {
                  const isExpired = sub.status === 'active' && new Date(sub.end_date) < new Date();
                  const effectiveStatus = isExpired ? 'expired' : sub.status;
                  const badge = STATUS_LABELS[effectiveStatus] || STATUS_LABELS.expired;
                  return (
                    <tr key={sub.id} style={{ borderBottom: '1px solid var(--border)', transition: 'background var(--transition)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{sub.provider_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{sub.provider_phone}</div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: 13 }}>{METHODS[sub.payment_method] || sub.payment_method || '—'}</td>
                      <td style={{ padding: '14px 16px', fontWeight: 700, fontSize: 14, color: 'var(--primary)', whiteSpace: 'nowrap' }}>{fmt(sub.amount)} FCFA</td>
                      <td style={{ padding: '14px 16px', fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmtDate(sub.start_date)}</td>
                      <td style={{ padding: '14px 16px', fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmtDate(sub.end_date)}</td>
                      <td style={{ padding: '14px 16px', fontSize: 13, color: 'var(--text-secondary)' }}>{sub.duration_days} j</td>
                      <td style={{ padding: '14px 16px' }}>
                        <code style={{ fontSize: 11, background: 'var(--bg)', padding: '3px 6px', borderRadius: 4, color: 'var(--text-muted)' }}>
                          {sub.payment_reference?.split('-').slice(0, 2).join('-') || '—'}
                        </code>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: badge.color, background: badge.bg, padding: '4px 10px', borderRadius: 'var(--radius-full)' }}>
                          {badge.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
